import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, CardContent, Grid, Paper, Stack } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SkeletonComponent from '../../Components/SkeletonComponent';
import { toast } from 'react-toastify';
import styles from './ExperimentEvaluationPage.module.css';
import { DocumentExplorer } from '../../Components/Shared/DocumentExplorer/DocumentExplorer';
import { ReorderableTable } from '../../Components/BodyComponent/Experiments/ReorderableTable/ReorderableTable';
import IconButton from '@mui/material/IconButton';
import { experimentsService } from '../../Services/Experiments.services';
import { Header } from '../../Components/Shared/Header/Header';
import ExperimentTitle from '../../Components/BodyComponent/Experiments/ExperimentTitle';
import EditableGoldStandard from '../../Components/BodyComponent/Experiments/EditableGoldStandard';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import ExperimentBreadcrumbs from '../../Components/BodyComponent/Experiments/ExperimentBreadcrumbs';
import ExperimentStatus from '../../Components/BodyComponent/Experiments/ExperimentStatus';
import RotateLeftRoundedIcon from '@mui/icons-material/RotateLeftRounded';
import Tooltip from '@mui/material/Tooltip';
import { useSelector } from 'react-redux';

function ExperimentEvaluationPage() {
    const { experimentId } = useParams();
    const { isAdmin } = useSelector((state) => state.auth);
    const [searchParams, setSearchParams] = useSearchParams();
    const [isSticky, setIsSticky] = useState(false);
    const trackerRef = useRef(null);
    const goldStandardSectionRef = useRef(null);
    const pageBodyRef = useRef(null);
    const rankHeaderRef = useRef(null);
    const runNameHeaderRef = useRef(null);
    const expectedValueHeaderRef = useRef(null);
    const checkboxHeaderRef = useRef(null);

    const navigate = useNavigate();
    const [isEvaluationLoading, setIsEvaluationLoading] = useState(true);
    const [isEvaluationStatusLoading, setIsEvaluationStatusLoading] = useState(true);
    const [documents, setDocuments] = useState([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(null);
    const [currentDataPointIndex, setCurrentDataPointIndex] = useState(null);
    const [evaluationStatus, setEvaluationStatus] = useState('');
    const [hasDocumentPreview, setHasDocumentPreview] = useState(false);
    const [items, setItems] = useState([]);
    const lowestRank = 1;

    const [goldStandardMarginLeft, setGoldStandardMarginLeft] = useState('initial');
    const [goldStandardParagraphWidth, setGoldStandardParagraphWidth] = useState('100%');
    const [activeGoldStandard, setActiveGoldStandard] = useState('');
    const [allGoldStandards, setAllGoldStandards] = useState({
        goldStandard: '',
        globalGoldStandard: '',
    });

    const getContentWidth = (element) => {
        if (!element) return 0;

        const styles = window.getComputedStyle(element);
        const paddingLeft = parseFloat(styles.paddingLeft);
        const paddingRight = parseFloat(styles.paddingRight);
        const fullWidth = element.getBoundingClientRect().width;

        return fullWidth - paddingLeft - paddingRight;
    };

    // resizeObserver for gold standard
    useEffect(() => {
        if (!rankHeaderRef.current || !runNameHeaderRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            const checkboxColumnWidth = checkboxHeaderRef.current?.clientWidth ?? 0;
            const rankColumnWidth = rankHeaderRef.current?.clientWidth ?? 0;
            const runNameColumnWidth = runNameHeaderRef.current?.clientWidth ?? 0;
            const expectedValueColumnWidth = getContentWidth(expectedValueHeaderRef.current) || '100%';
            setGoldStandardMarginLeft(`${rankColumnWidth + runNameColumnWidth + checkboxColumnWidth}px`);
            setGoldStandardParagraphWidth(expectedValueColumnWidth);
        });

        resizeObserver.observe(rankHeaderRef.current);
        resizeObserver.observe(runNameHeaderRef.current);

        const rankColumnWidth = rankHeaderRef.current?.clientWidth ?? 0;
        const runNameColumnWidth = runNameHeaderRef.current?.clientWidth ?? 0;
        const expectedValueColumnWidth = getContentWidth(expectedValueHeaderRef.current) || '100%';
        setGoldStandardMarginLeft(`${rankColumnWidth + runNameColumnWidth}px`);
        setGoldStandardParagraphWidth(expectedValueColumnWidth);

        return () => resizeObserver.disconnect();
    }, [items]);

    // sticky header
    useEffect(() => {
        const handleScroll = () => {
            if (trackerRef.current) {
                const rect = trackerRef.current.getBoundingClientRect();
                const isOutOfScreen = rect.top <= 92;
                setIsSticky(isOutOfScreen);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // set current document and datapoint
    useEffect(() => {
        const documentId = searchParams.get('documentId');
        const datapointId = searchParams.get('datapointId');

        if (documentId && datapointId && documents.length > 0) {
            const documentIndex = documents.findIndex((doc) => doc.id === documentId);

            if (documentIndex !== -1) {
                const document = documents[documentIndex];
                const datapointIndex = document.datapoints.findIndex((dp) => dp.id === datapointId);

                if (datapointIndex !== -1) {
                    setCurrentDocumentIndex(documentIndex);
                    setCurrentDataPointIndex(datapointIndex);
                }
            }
        }
    }, [documents, searchParams]);

    const sortByRank = (a, b) => {
        // If both have ranks, sort by rank (lowest first)
        if (a.rank !== null && b.rank !== null) {
            return a.rank - b.rank;
        }
        // If one has null rank, move it to the end
        if (a.rank === null && b.rank !== null) return 1;
        if (a.rank !== null && b.rank === null) return -1;
        // If both have null ranks, maintain their relative order
        return 0;
    };

    const handleDatapointClick = (documentId, datapointId) => {
        const documentIndex = documents.findIndex((doc) => doc.id === documentId);

        if (documentIndex !== -1) {
            const document = documents[documentIndex];
            const datapointIndex = document.datapoints.findIndex((dp) => dp.id === datapointId);

            if (datapointIndex !== -1) {
                setCurrentDocumentIndex(documentIndex);
                setCurrentDataPointIndex(datapointIndex);
                setSearchParams({ documentId, datapointId });
            }
        }
    };

    const handleRankReset = (row) => {
        setItems((prev) => {
            const updatedList = prev.map((item) =>
                item.selected ? { ...item, rank: null, selected: false } : { ...item, selected: false }
            );
            updateExperimentEvaluation(updatedList);
            return updatedList;
        });
    };

    const fetchEvaluation = async (documentId, datapointId, experimentId) => {
        setIsEvaluationLoading(true);

        try {
            const data = await experimentsService.getExperimentEvaluation(experimentId, documentId, datapointId);
            const sortedItems = [...data.rankings].sort(sortByRank);
            setHasDocumentPreview(data.document_has_preview ?? false);
            setAllGoldStandards({
                globalGoldStandard: data.global_gold_standard ?? '',
                goldStandard: data.gold_standard ?? '',
            });
            setActiveGoldStandard(data.global_gold_standard ?? data.gold_standard);
            setItems(sortedItems);
        } catch (err) {
            console.error('Error fetching evaluation:', err);
            toast.error('Failed to load evaluation. Please try again later.');
        } finally {
            setIsEvaluationLoading(false);
        }
    };

    const switchGoldStandard = () => {
        const { goldStandard, globalGoldStandard } = allGoldStandards;

        if (!goldStandard && !globalGoldStandard) return;

        const isCurrentlyGlobal = activeGoldStandard === globalGoldStandard;
        setActiveGoldStandard(isCurrentlyGlobal ? goldStandard : globalGoldStandard);
    };

    useEffect(() => {
        if (!experimentId) return;
        const documentId = documents[currentDocumentIndex]?.id;
        const datapointId = documents[currentDocumentIndex]?.datapoints[currentDataPointIndex]?.id;

        if (!documentId || !datapointId) {
            setHasDocumentPreview(false);
            setIsEvaluationLoading(false);
            setActiveGoldStandard(null);
            setItems([]);
            return;
        }

        fetchEvaluation(documentId, datapointId, experimentId);
    }, [currentDocumentIndex, currentDataPointIndex, experimentId]);

    const tableHeaders = [
        {
            label: 'Rank',
            align: 'center',
            ref: rankHeaderRef,
        },
        {
            label: 'Run',
            align: 'center',
            ref: runNameHeaderRef,
        },
        {
            label: 'Extracted value',
            align: 'left',
            ref: expectedValueHeaderRef,
        },
    ];

    const convertToCells = (row, rowIndex) => [
        {
            align: 'center',
            data: (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '8px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <div className={styles.rankContainer}>
                            {items.map((item, index) => (
                                <button
                                    key={index}
                                    className={`${styles.rankButton} ${
                                        index + 1 == row.rank ? styles.selectedRankButton : ''
                                    }`}
                                    onClick={() => {
                                        selectSelfIfNoSelections(rowIndex);
                                        handleSetRank(index + 1);
                                    }}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                        {Number.isInteger(row.rank) && (
                            <Tooltip title="Unset rank">
                                <IconButton
                                    sx={{ padding: '8px 0px' }}
                                    onClick={() => {
                                        selectSelfIfNoSelections(rowIndex);
                                        handleRankReset(row);
                                    }}
                                >
                                    <RotateLeftRoundedIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                </div>
            ),
        },
        {
            align: 'center',
            style: { width: '20%' },
            data: row.run_name || 'Unknown run',
        },
        {
            style: {
                width: '80%',
                color: !row.extracted_value ? 'red' : 'initial',
            },
            data: row.extracted_value || 'Missing',
        },
        {
            align: 'center',
            data: (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '5px',
                    }}
                >
                    {(row.extracted_value || 'Missing') !== activeGoldStandard ? (
                        <Tooltip
                            title="Save as a gold standard"
                            placement="top"
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: { offset: [0, -14] },
                                        },
                                    ],
                                },
                            }}
                        >
                            <IconButton onClick={() => handleGoldStandardUpdate(row.extracted_value || 'Missing')}>
                                <img src={`${process.env.PUBLIC_URL}/images/icon-save-black.svg`} alt="Black save icon" />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <div style={{ marginRight: 35 }}></div>
                    )}
                    <Tooltip
                        title="Save as an example"
                        placement="top"
                        slotProps={{
                            popper: {
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: { offset: [0, -14] },
                                    },
                                ],
                            },
                        }}
                    >
                        <IconButton onClick={() => handleDataPointSaveExample(row.extracted_value || 'Missing')}>
                            <img src={`${process.env.PUBLIC_URL}/images/icon-list-alt-add.svg`} alt="List alt add icon" />
                        </IconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const tableRows = items.map((row, rowIndex) => ({
        cells: convertToCells(row, rowIndex),
        key: row.run_id,
    }));

    const fetchEvaluationStatus = async () => {
        try {
            const evaluationStatus = await experimentsService.getExperimentEvaluationStatus(experimentId);
            setEvaluationStatus(evaluationStatus['status']);
            setDocuments(evaluationStatus['documents']);

            return evaluationStatus;
        } catch (err) {
            console.error('Error fetching evaluation status:', err);
            toast.error('Failed to load evaluation status. Please try again later.');
        } finally {
            setIsEvaluationStatusLoading(false);
        }
    };

    useEffect(() => {
        fetchEvaluationStatus().then((status) => {
            if (status['documents'].length > 0) {
                setCurrentDocumentIndex(0);
                setCurrentDataPointIndex(0);
            }
        });
    }, [experimentId]);

    const getCurrentDocument = useCallback(() => documents[currentDocumentIndex], [documents, currentDocumentIndex]);

    const getCurrentDataPoint = useCallback(
        () => getCurrentDocument()?.datapoints[currentDataPointIndex] ?? null,
        [getCurrentDocument, currentDataPointIndex]
    );

    const handleDataPointSaveExample = async (value) => {
        const documentId = getCurrentDocument()?.id;
        const datapointId = getCurrentDataPoint()?.id;

        if (!documentId || !datapointId) return;

        try {
            await experimentsService.saveDataPointExample(documentId, datapointId, value);
            toast.success('Saved as an example');
        } catch {
            toast.error('Failed to save as an example');
        }
    };

    const handleGoldStandardUpdate = async (value) => {
        const documentId = getCurrentDocument()?.id;
        const datapointId = getCurrentDataPoint()?.id;

        if (!documentId || !datapointId) return;

        await experimentsService.updateGoldStandard(documentId, datapointId, value);
        await getUpdatedGoldStandards(documentId, datapointId);

        setActiveGoldStandard(value);
        toast.success('Gold standard updated');
    };

    const getUpdatedGoldStandards = async (documentId, datapointId) => {
        const values = await experimentsService.getExperimentEvaluation(experimentId, documentId, datapointId);

        const newStandards = {
            globalGoldStandard: values.global_gold_standard ?? '',
            goldStandard: values.gold_standard ?? '',
        };

        setAllGoldStandards(newStandards);
    };

    const handlePreviousDocument = () => {
        if (currentDocumentIndex > 0) {
            const previousDocument = documents[currentDocumentIndex - 1];
            setCurrentDocumentIndex(currentDocumentIndex - 1);
            setCurrentDataPointIndex(0);
            setSearchParams({
                documentId: previousDocument.id,
                datapointId: searchParams.get('datapointId') ?? previousDocument.datapoints[0].id,
            });
        }
    };

    const handleNextDocument = () => {
        if (currentDocumentIndex < documents.length - 1) {
            const nextDocument = documents[currentDocumentIndex + 1];
            setCurrentDocumentIndex(currentDocumentIndex + 1);
            setSearchParams({
                documentId: nextDocument.id,
                datapointId: searchParams.get('datapointId') ?? nextDocument.datapoints[0].id,
            });
        }
    };

    const handlePreviousDataPoint = () => {
        const currentDocument = getCurrentDocument();
        if (currentDocument) {
            if (currentDataPointIndex > 0) {
                setCurrentDataPointIndex(currentDataPointIndex - 1);
                setSearchParams({
                    documentId: currentDocument.id,
                    datapointId: currentDocument.datapoints[currentDataPointIndex - 1].id,
                });
            } else if (currentDocumentIndex > 0) {
                const previousDocument = documents[currentDocumentIndex - 1];
                setCurrentDocumentIndex(currentDocumentIndex - 1);
                setCurrentDataPointIndex(previousDocument.datapoints.length - 1);
                setSearchParams({
                    documentId: previousDocument.id,
                    datapointId: previousDocument.datapoints[previousDocument.datapoints.length - 1].id,
                });
            }
        }
    };

    const handleNextDataPoint = () => {
        const currentDocument = getCurrentDocument();

        if (currentDocument) {
            if (currentDataPointIndex < currentDocument.datapoints.length - 1) {
                setCurrentDataPointIndex(currentDataPointIndex + 1);
                setSearchParams({
                    documentId: currentDocument.id,
                    datapointId: currentDocument.datapoints[currentDataPointIndex + 1].id,
                });
            } else if (currentDocumentIndex < documents.length - 1) {
                const nextDocument = documents[currentDocumentIndex + 1];

                setCurrentDocumentIndex(currentDocumentIndex + 1);
                setCurrentDataPointIndex(0);
                setSearchParams({
                    documentId: nextDocument.id,
                    datapointId: nextDocument.datapoints[0].id,
                });
            }
        }
    };

    const handleSetSelectedRanks = () => {
        setItems((prevItems) => {
            const withNewRanks = prevItems.map((item) => ({ ...item, rank: 1, selected: false }));
            updateExperimentEvaluation(withNewRanks);
            return withNewRanks;
        });
    };

    const handleSetSelectedUnsatisfactory = () => {
        setItems((prevItems) => {
            const withNewRanks = prevItems.map((item) =>
                ({ ...item, rank: items.length, selected: false })
            );
            updateExperimentEvaluation(withNewRanks);
            return withNewRanks;
        });
    };

    const reorderForward = (rows, startIndex, endIndex) => {
        const temp = rows[startIndex];

        for (let i = startIndex; i < endIndex; i++) {
            rows[i] = rows[i + 1];
        }

        rows[endIndex - 1] = temp;

        return rows;
    };

    const reorderBackward = (rows, startIndex, endIndex) => {
        const temp = rows[startIndex];

        for (let i = startIndex; i > endIndex; i--) {
            rows[i] = rows[i - 1];
        }

        rows[endIndex] = temp;

        return rows;
    };

    const updateExperimentEvaluation = (updatedList) => {
        const evaluationDetails = updatedList.map((item) => ({
            rank: item.rank,
            run_id: item.run_id,
        }));
        const documentId = getCurrentDocument().id;
        const datapointId = getCurrentDataPoint().id;

        experimentsService
            .updateExperimentEvaluation(experimentId, documentId, datapointId, evaluationDetails)
            .then(() => fetchEvaluationStatus());
    };

    const handleSetRank = (newRank) => {
        setItems((prev) => {
            const updatedList = prev.map((item) =>
                item.selected ? { ...item, rank: newRank, selected: false } : item
            );
            // const normalized = normalizeAndSort(updatedList);
            updateExperimentEvaluation(updatedList);
            return updatedList;
        });
    };

    const handleTableReorder = (startIndex, endIndex) => {
        const reorderList = (rows, startIndex, endIndex) => {
            if (startIndex < endIndex) {
                return reorderForward([...rows], startIndex, endIndex);
            } else if (startIndex > endIndex) {
                return reorderBackward([...rows], startIndex, endIndex);
            }
            return rows;
        };

        const fixedEndIndex = endIndex > items.length - 1 ? items.length - 1 : endIndex === -1 ? 0 : endIndex;

        setItems((prev) => {
            const updatedList = prev.map((item, index) =>
                index === startIndex ? { ...item, rank: fixedEndIndex + 1 } : item
            );
            const reorderedList = reorderList([...updatedList], startIndex, endIndex);
            updateExperimentEvaluation(reorderedList);
            return reorderedList;
        });
    };

    const handleViewDocument = async () => {
        const currentDocument = getCurrentDocument();
        if (currentDocument) {
            try {
                const { data, contentType } = await experimentsService.downloadExperimentDocument(experimentId, currentDocument.id);
                const blob = new Blob([data], { type: contentType });
                const blobUrl = URL.createObjectURL(blob);
                window.open(blobUrl, '_blank');
            } catch (e) {
                console.error(e);
            }
        }
    };

    const renderNavigation = () => (
        <div className={styles.header}>
            <div className={styles.docNavigation}>
                <Button
                    className={styles.entityNavButton}
                    variant="contained"
                    onClick={handlePreviousDocument}
                    disabled={currentDocumentIndex === 0}
                >
                    Previous Document
                </Button>
    
                <div className={styles.selectedEntityTitle}>
                    {getCurrentDocument()?.name}
                </div>
    
                <IconButton
                    disabled={!hasDocumentPreview}
                    onClick={handleViewDocument}
                    sx={{
                        backgroundColor: (theme) => theme.palette.primary.main,
                        borderRadius: '5px',
                        '&.Mui-disabled': { backgroundColor: 'rgb(224,224,224)' },
                        '&:hover': {
                            backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                    }}
                >
                    <VisibilityRoundedIcon
                        sx={{ color: (theme) => theme.palette.primary.contrastText }}
                    />
                </IconButton>
    
                <Button
                    className={styles.entityNavButton}
                    variant="contained"
                    onClick={handleNextDocument}
                    disabled={currentDocumentIndex === documents.length - 1}
                >
                    Next Document
                </Button>
            </div>
    
            <div className={styles.dataPointNavigation}>
                <Button
                    className={styles.entityNavButton}
                    variant="contained"
                    onClick={handlePreviousDataPoint}
                    disabled={currentDataPointIndex === 0 && currentDocumentIndex === 0}
                >
                    Previous Datapoint
                </Button>
    
                <div className={styles.selectedEntityTitle}>
                    {getCurrentDataPoint()?.name}
                </div>
    
                <Button
                    className={styles.entityNavButton}
                    variant="contained"
                    onClick={handleNextDataPoint}
                    disabled={
                        currentDataPointIndex ===
                            getCurrentDocument()?.datapoints.length - 1 &&
                        currentDocumentIndex === documents.length - 1
                    }
                >
                    Next Datapoint
                </Button>
            </div>
        </div>
    );

    return (
        <Grid item xs={12} sm={12} md={12} sx={{ mt: 2, mb: 2 }}>
            <Card component={Paper}>
                <CardContent>
                    <div className="header">
                        <Header
                            title={
                                <ExperimentBreadcrumbs
                                    crumbs={[
                                        {
                                            label: 'Experiments',
                                            href: '/experiments',
                                        },
                                        {
                                            label: <ExperimentTitle experimentId={experimentId} />,
                                        },
                                    ]}
                                    pageTitle={
                                        <>
                                            Evaluation <ExperimentStatus status={evaluationStatus} />
                                        </>
                                    }
                                />
                            }
                            actionButtons={[
                                isAdmin && (
                                    <Button
                                        key="go-to-docs-button"
                                        variant="outlined"
                                        onClick={() => {
                                            navigate(`/experiments/${experimentId}/docs`);
                                        }}
                                    >
                                        Docs
                                    </Button>
                                ),
                                isAdmin && (
                                    <Button
                                        key="go-to-runs-button"
                                        variant="outlined"
                                        onClick={() => {
                                            navigate(`/experiments/${experimentId}/runs`);
                                        }}
                                    >
                                        Runs
                                    </Button>
                                ),
                                isAdmin && (
                                    <Button
                                        key="go-to-results-button"
                                        variant="contained"
                                        onClick={() => {
                                            navigate(`/experiments/${experimentId}/results`);
                                        }}
                                    >
                                        Results
                                    </Button>
                                ),
                                <Button
                                    key="go-to-status-button"
                                    variant="contained"
                                    onClick={() => {
                                        navigate(`/experiments/${experimentId}/status`);
                                    }}
                                >
                                    Progress
                                </Button>,
                            ]}
                        />
                    </div>
                    <div className={styles.page}>
                        <div className={styles.sidenavContainer}>
                            {isEvaluationStatusLoading ? (
                                <SkeletonComponent />
                            ) : (
                                <DocumentExplorer
                                    documents={documents}
                                    onDatapointClick={handleDatapointClick}
                                    selectedDocumentId={getCurrentDocument()?.id}
                                    selectedDatapointId={getCurrentDataPoint()?.id}
                                />
                            )}
                        </div>
                        <div className={styles.content}>
                            {isEvaluationLoading ? (
                                <SkeletonComponent />
                            ) : !items?.length ? (
                                <Stack sx={{ width: '100%' }} spacing={2}>
                                    <Alert severity="warning">There are not enough runs to evaluate!</Alert>
                                </Stack>
                            ) : (
                                <>
                                    {!isSticky && renderNavigation()}
                                    <div ref={pageBodyRef} className={styles.body}>
                                        <div ref={trackerRef} style={{ height: '1px' }} />
                                        {isSticky && (
                                            <div
                                                style={{
                                                    height: goldStandardSectionRef.current?.clientHeight,
                                                }}
                                            />
                                        )}
                                        <div
                                            ref={goldStandardSectionRef}
                                            style={{ width: pageBodyRef.current?.clientWidth ?? '100%' }}
                                            className={`${isSticky
                                                ? styles.stickyGoldStandard
                                                : ''}`
                                            }
                                        >
                                            <EditableGoldStandard
                                                value={activeGoldStandard}
                                                onUpdate={handleGoldStandardUpdate}
                                                marginLeft={goldStandardMarginLeft}
                                                width={goldStandardParagraphWidth}
                                                switchGoldStandard={switchGoldStandard}
                                                globalGoldStandard={allGoldStandards.globalGoldStandard}
                                            />
                                        </div>
                                        <div className={styles.tableContainer}>
                                            <div className={styles.table}>
                                                <ReorderableTable
                                                    headers={tableHeaders}
                                                    rows={tableRows}
                                                    onReorder={handleTableReorder}
                                                />
                                            </div>
                                            <div className={styles.rankButtonContainer}>
                                                <Button
                                                    key="set-selected-button"
                                                    className={styles.setSelectedButton}
                                                    variant="outlined"
                                                    disabled={items.every((item) => item.rank === lowestRank)}
                                                    onClick={handleSetSelectedRanks}
                                                >
                                                    All Equally Good
                                                </Button>
                                                <Button
                                                    key="selected-unsatisfactory-button"
                                                    className={styles.setSelectedButton}
                                                    variant="outlined"
                                                    disabled={items.every((item) => item.rank === items.length)}
                                                    onClick={handleSetSelectedUnsatisfactory}
                                                >
                                                    All Equally Bad
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                            {isSticky && renderNavigation()}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Grid>
    );

    function selectSelfIfNoSelections(rowIndex) {
        if (!items[rowIndex].selected) {
            setItems((prev) => {
                const updatedList = prev.map((item, index) => {
                    if (index === rowIndex) {
                        return {
                            ...item,
                            selected: true,
                        };
                    }
                    return {
                        ...item,
                        selected: false,
                    };
                });
                return updatedList;
            });
        }
    }
}

export default ExperimentEvaluationPage;
